import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RatingGroup } from '#app/domain/funnel/ratingTableTypes';

interface SelectedClientsState {
    ids: number[];
    loading: boolean;
    locallySelectedClients: number[] | null;
    excludedRatings: number[];
    excludedGroups: Record<number, RatingGroup>;
}

const initialState: SelectedClientsState = {
    ids: [],
    loading: false,
    locallySelectedClients: null,
    excludedRatings: [],
    excludedGroups: {},
};

const selectedClientIdsSlice = createSlice({
    name: 'selectedClientIds',
    initialState,
    reducers: {
        resetSelectedClientsState(state) {
            Object.assign(state, initialState);
        },

        resetFiltersToAllClients(state, action: PayloadAction<number[]>) {
            state.ids = action.payload;
            state.loading = false;
            state.locallySelectedClients = null;
            state.excludedRatings = [];
            state.excludedGroups = {};
        },

        setSelectedClientIds(state, action: PayloadAction<number[]>) {
            state.ids = action.payload;
        },
        toggleClientSelection(state, action: PayloadAction<number>) {
            const clientId = action.payload;
            if (state.ids.includes(clientId)) {
                state.ids = state.ids.filter((existingId) => existingId !== clientId);
            } else {
                state.ids = [...state.ids, clientId];
            }
        },
        clearSelectedClientIds(state) {
            state.ids = [];
        },
        setSelectedClientsLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setLocallySelectedClients(state, action: PayloadAction<number[] | null>) {
            state.locallySelectedClients = action.payload;
        },
        clearLocallySelectedClients(state) {
            state.locallySelectedClients = null;
        },
        addExcludedRating(state, action: PayloadAction<number>) {
            if (!state.excludedRatings.includes(action.payload)) {
                state.excludedRatings.push(action.payload);
            }
        },
        removeExcludedRating(state, action: PayloadAction<number>) {
            state.excludedRatings = state.excludedRatings.filter(
                (rating) => rating !== action.payload
            );
        },
        clearExcludedRatings(state) {
            state.excludedRatings = [];
        },
        setExcludedGroup(state, action: PayloadAction<{ rating: number; group: RatingGroup }>) {
            const group = action.payload.group;
            if (group && typeof group === 'object' && !Array.isArray(group) && group !== null) {
                state.excludedGroups[action.payload.rating] = group;
            }
        },
        removeExcludedGroup(state, action: PayloadAction<number>) {
            delete state.excludedGroups[action.payload];
        },
        clearExcludedGroups(state) {
            state.excludedGroups = {};
        },
    },
});

export const {
    resetSelectedClientsState,
    resetFiltersToAllClients,
    setSelectedClientIds,
    toggleClientSelection,
    clearSelectedClientIds,
    setSelectedClientsLoading,
    setLocallySelectedClients,
    clearLocallySelectedClients,
    addExcludedRating,
    removeExcludedRating,
    clearExcludedRatings,
    setExcludedGroup,
    removeExcludedGroup,
    clearExcludedGroups,
} = selectedClientIdsSlice.actions;

export default selectedClientIdsSlice.reducer;